import { useRef } from "react";
import { Check, Loader2, Lock, MapPin, SearchX, X } from "lucide-react";
import { toast } from "sonner";

import { Input } from "../../components/ui/input";
import BuscaBairro from "./BuscaBairro";
import { useCepEndereco } from "./useCepEndereco";

const reais = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const CAMPO = "h-11 bg-background";

function Campo({ rotulo, obrigatorio, children, className = "" }) {
  return (
    <label className={`block space-y-1.5 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground">
        {rotulo}{obrigatorio && <span className="text-orange-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

const SITUACAO = {
  unico: { Icone: Lock, texto: "CEP único da cidade — já preenchido", cor: "text-sky-600 dark:text-sky-400 bg-sky-500/10" },
  buscando: { Icone: Loader2, texto: "Buscando o CEP…", cor: "text-muted-foreground bg-muted", girar: true },
  achou: { Icone: Check, texto: "Rua e bairro preenchidos pelo CEP — confira", cor: "text-green-600 dark:text-green-400 bg-green-500/10" },
  so_bairro: { Icone: Check, texto: "Bairro achado pelo CEP — preencha a rua", cor: "text-green-600 dark:text-green-400 bg-green-500/10" },
  so_rua: { Icone: MapPin, texto: "Rua preenchida — escolha o bairro na lista", cor: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
  nao_achou: { Icone: SearchX, texto: "CEP não encontrado — preencha à mão", cor: "text-amber-600 dark:text-amber-400 bg-amber-500/10" },
};

function SeloCep({ situacao }) {
  const s = SITUACAO[situacao];
  if (!s) return <p className="text-xs text-muted-foreground pb-3">Digite o CEP e a rua e o bairro vêm sozinhos</p>;
  const { Icone } = s;
  return (
    <span className={`inline-flex items-center gap-1.5 self-center px-2.5 py-1 rounded-full text-xs font-medium ${s.cor}`}>
      <Icone className={`w-3.5 h-3.5 ${s.girar ? "animate-spin" : ""}`} /> {s.texto}
    </span>
  );
}

function Resumo({ endereco, taxa }) {
  if (!endereco.rua || !endereco.bairro) return null;
  const linha = [`${endereco.rua}${endereco.numero ? `, ${endereco.numero}` : ""}`, endereco.complemento, endereco.bairro].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-3 px-5 py-3 border-t bg-green-500/5">
      <MapPin className="w-4 h-4 text-green-500 shrink-0" />
      <p className="flex-1 min-w-0 text-sm truncate"><span className="font-semibold">{linha}</span>{endereco.cep && <span className="text-muted-foreground"> · {endereco.cep}</span>}</p>
      <span className="shrink-0 text-xs font-semibold text-green-600 dark:text-green-400">Taxa {reais(taxa)}</span>
    </div>
  );
}

/**
 * Cadastro de endereço na etapa Entrega do Novo Pedido (redesenho 23/09/2026):
 * CEP primeiro; com CEP único ele já vem preenchido, senão o CEP puxa rua e
 * bairro. O bairro continua vindo só da lista cadastrada — é ele que dá a taxa.
 */
export default function FormNovoEndereco({ endereco, setEndereco, bairros, taxaEntrega, setTaxaEntrega, onFechar, onConcluir }) {
  const ruaRef = useRef(null);
  const caixa = useRef(null);
  // Teclado (pedido do dono, 30/09/2026): Enter pula de campo em campo
  // (CEP → Rua → Número → Bairro → Complemento → Referência); no último, confere
  // os obrigatórios e avança para o Pagamento (`onConcluir`).
  const campos = () => [...(caixa.current?.querySelectorAll("[data-campo-endereco]") || [])].filter((el) => !el.readOnly);
  const concluir = () => {
    const falta = !endereco.rua?.trim() ? "rua" : !endereco.numero?.trim() ? "numero" : !endereco.bairro ? "bairro" : null;
    if (falta) {
      toast.error(falta === "bairro" ? "Escolha o bairro" : `Preencha ${falta === "rua" ? "a rua" : "o número"}`);
      const el = caixa.current?.querySelector(`[data-campo="${falta}"]`);
      (el?.matches("input") ? el : el?.querySelector("input"))?.focus();
      return;
    }
    if (onConcluir) onConcluir();
  };
  const irProximo = (atual) => {
    const lista = campos();
    const i = lista.indexOf(atual);
    if (i >= 0 && i < lista.length - 1) lista[i + 1].focus();
    else concluir();
  };
  const tecla = (e) => {
    if (e.key !== "Enter" || e.defaultPrevented || !e.target.closest?.("[data-campo-endereco]")) return;
    e.preventDefault();
    irProximo(e.target.closest("[data-campo-endereco]"));
  };
  const { cepUnico, situacao, aoDigitarCep, escolherBairro } = useCepEndereco({ endereco, setEndereco, bairros, setTaxaEntrega, ruaRef });
  const mudar = (campo) => (e) => setEndereco((atual) => ({ ...atual, [campo]: e.target.value }));
  const lista = bairros || [];

  return (
    <div ref={caixa} onKeyDown={tecla} className="rounded-2xl border bg-card overflow-hidden shadow-sm" data-e2e="novo-endereco">
      <div className="flex items-center gap-3 px-5 py-4 border-b">
        <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-500 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Novo endereço</p>
          <p className="text-xs text-muted-foreground">Comece pelo CEP</p>
        </div>
        <button type="button" onClick={onFechar} title="Cancelar novo endereço" className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"><X className="w-5 h-5" /></button>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Campo rotulo="CEP" className="w-40">
            <Input value={endereco.cep} onChange={(e) => aoDigitarCep(e.target.value)} readOnly={!!cepUnico} inputMode="numeric" placeholder="00000-000" autoFocus={!cepUnico}
              className={`${CAMPO} font-semibold tabular-nums tracking-wide ${cepUnico ? "opacity-80 cursor-not-allowed" : ""}`} data-e2e="cep" data-campo-endereco />
          </Campo>
          <SeloCep situacao={situacao} />
        </div>

        <div className="grid grid-cols-[1fr_7rem] sm:grid-cols-[1fr_9rem] gap-3">
          <Campo rotulo="Rua" obrigatorio>
            <Input ref={ruaRef} value={endereco.rua} onChange={mudar("rua")} placeholder="Nome da rua" className={CAMPO} data-campo-endereco data-campo="rua" data-e2e="end-rua" />
          </Campo>
          <Campo rotulo="Número" obrigatorio>
            <Input value={endereco.numero} onChange={mudar("numero")} placeholder="Nº" className={CAMPO} data-campo-endereco data-campo="numero" data-e2e="end-numero" />
          </Campo>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo rotulo="Bairro" obrigatorio>
            <span data-campo="bairro" className="contents">
              <BuscaBairro bairros={lista} valor={endereco.bairro || ""} onEscolher={escolherBairro} onEscolhido={irProximo} className={CAMPO} />
            </span>
          </Campo>
          <Campo rotulo="Complemento">
            <Input value={endereco.complemento} onChange={mudar("complemento")} placeholder="Apto, bloco, casa…" className={CAMPO} data-campo-endereco data-e2e="end-complemento" />
          </Campo>
        </div>

        <Campo rotulo="Ponto de referência">
          <Input value={endereco.referencia || ""} onChange={mudar("referencia")} placeholder="Ex.: portão azul, ao lado da padaria" className={CAMPO} data-campo-endereco data-e2e="end-referencia" />
        </Campo>
      </div>

      <Resumo endereco={endereco} taxa={taxaEntrega} />
    </div>
  );
}
